import isApp from "shared/util/isApp";
import _str from "shared/util/_str";
import cloneDeep from "lodash/cloneDeep";
import findIndex from "lodash/findIndex";

import {
	SAVE_CROP,
	RESET_UPLOADER,
	UPLOAD_FILE_SUCCESS,
	UPLOAD_DETAILS_SUCCESS,
} from "ducks/photo_upload";

import {
	REORDER_PHOTOS_SUCCESS,
} from "ducks/photo_organizer";

import {
	REQUIREMENTS,
	STEPS,
	USER_THUMB_SIZE,
} from "apps/onboarding/util/constants";

import * as OnboardingApi from "apps/onboarding/util/web";
import AppPhoneCommand from "shared/util/AppPhoneCommand";
import { getRequirementSentence, getFirstIncompletedStep } from "apps/onboarding/util/helper";

// ////////////////////////
// ACTION TYPES //////////
// //////////////////////

export const INITIALIZE_ONBOARDING = "ONBOARDING.INITIALIZE_ONBOARDING";
export const NEXT_STEP = "ONBOARDING.NEXT_STEP";
export const PREVIOUS_STEP = "ONBOARDING.PREVIOUS_STEP";
export const FINISH = "ONBOARDING.FINISH";
export const SHOW_ERROR = "ONBOARDING.SHOW_ERROR";
export const COMPLETE_ONBOARDING_SUCCESS = "ONBOARDING.COMPLETE_ONBOARDING_SUCCESS";
export const COMPLETE_ONBOARDING_FAILURE = "ONBOARDING.COMPLETE_ONBOARDING_FAILURE";
export const UPDATE_REQUIREMENT_PROGRESS = "ONBOARDING.UPDATE_REQUIREMENT_PROGRESS";
export const RECORD_QUESTION_RESPONSE = "ONBOARDING.RECORD_QUESTION_RESPONSE";
export const SUBMIT_QUESTION_RESPONSE = "ONBOARDING.SUBMIT_QUESTION_RESPONSE";
export const SUBMIT_QUESTION_CARD_RESPONSE = "ONBOARDING.SUBMIT_QUESTION_CARD_RESPONSE";
export const NEXT_ON_QUESTION_INTRO = "ONBOARDING.NEXT_ON_QUESTION_INTRO";
export const NEXT_ON_WIW_FIELD = "ONBOARDING.NEXT_ON_WIW_FIELD";
export const UPDATE_WIW_FIELD_DATA = "ONBOARDING.UPDATE_WIW_FIELD_DATA";
export const ADD_EDUCATION_OCCUPATION_STEP = "ONBOARDING.ADD_EDUCATION_OCCUPATION_STEP";
export const PREVIOUS_ON_WIW_FIELD = "ONBOARDING.PREVIOUS_ON_WIW_FIELD";
export const NEXT_ON_STEP_INTRO = "ONBOARDING.NEXT_ON_STEP_INTRO";
export const COMPLETE_QUESTION_CARDS = "ONBOARDING.COMPLETE_QUESTION_CARDS";
export const SET_ACTIVE_STEP = "ONBOARDING.SET_ACTIVE_STEP";
export const CONFIRM_RECAPTCHA_SUCCESS = "ONBOARDING.CONFIRM_RECAPTCHA_SUCCESS";

// ////////////////////////
// INITIAL STATE /////////
// //////////////////////

export const INITIAL_STATE = {
	// From the server
	configName: "",
	user: null,
	userThumb: null,
	redirect: {
		url: "/profile?onboarding_complete=1",
		message: "Finding you great people!",
	},
	canSkip: true,
	steps: [],
	showFacebookConnect: false,
	sortFacebookPhotosByLikes: false,
	stepsCompleted: [],
	stepNames: {},

	// JS state
	activeStep: null,
	isFinished: false,
	error: null,
	requirementProgress: {},
	activityCount: {},
	isViewingPreviousStep: false,

	// is completed === provisional turned off
	isCompleted: false,
	isCompletedError: "",
};

function getInitialState() {
	const state = typeof window !== "undefined" && window.OnboardingParams ?
		{ ...INITIAL_STATE, ...window.OnboardingParams } :
		INITIAL_STATE;

	if (state.user) {
		const firstPhoto = state.user.thumbs[0];

		if (firstPhoto && firstPhoto.info.has_photos) {
			state.userThumb = firstPhoto[USER_THUMB_SIZE];
		}
	}

	return state;
}

let skippedAny = false;

function formatParamsForMarkingStepCompletion(
	configName, activeStep, upcomingStep, params, activityCount,
) {
	const activeStepLog = {};
	const paramKeys = Object.keys(params);

	activeStepLog.name = activeStep.name;
	activeStepLog.index = activeStep.index;
	activeStepLog.step = activeStep.statname;
	activeStepLog.next_step = upcomingStep ? upcomingStep.statname : null;
	activeStepLog.skipped = !!params.skipped;
	activeStepLog.config_name = configName;
	skippedAny = skippedAny || activeStepLog.skipped;

	if (activeStep.name === STEPS.QUESTIONS) {
		activeStepLog.requirement = activeStep.questionsRequired;
	}

	if (activeStepLog.progress) {
		activeStepLog.progress = activityCount || 0;
	}

	if (!activeStepLog.next_step) {
		activeStepLog.skipped_any = skippedAny;
	}

	paramKeys.forEach((key) => {
		activeStepLog[key] = params[key];
	});

	return activeStepLog;
}


// ////////////////////////
// REDUCER ///////////////
// //////////////////////

// define initial state here becuase redux/init actions fire with undefined state
// so getInitialState gets called multiple times
const initialState = getInitialState();

export function reducer(state = initialState, action) {
	switch (action.type) {
		case INITIALIZE_ONBOARDING: {
			const data = action.payload;

			if (data.user) {
				const firstPhoto = data.user.thumbs[0];

				if (firstPhoto && firstPhoto.info.has_photos) {
					data.userThumb = firstPhoto[USER_THUMB_SIZE];
				}
			}

			return {
				...state,
				...data,
			};
		}

		case NEXT_STEP: {
			let activeStep = state.activeStep;
			let isFinished = state.isFinished;
			const { stepsCompleted } = state;
			const params = action.params || {};

			if (activeStep) {
				if (state.steps.length > activeStep.index + 1) {
					activeStep = state.steps[state.activeStep.index + 1];
				} else {
					isFinished = true;
					params.isLastStep = true;
				}

				const markStepCompleteParams = formatParamsForMarkingStepCompletion(
					state.configName,
					state.activeStep,
					isFinished ? null : activeStep,
					params,
					state.activityCount[state.activeStep.name],
				);

				OnboardingApi.markOnboardingStepAsCompleted(markStepCompleteParams).catch((err) => err);
			} else {
				// for lookup ease, make a map of completed steps
				const stepsCompletedMap = stepsCompleted.reduce((memo, name) => {
					return { ...memo, [name]: 1 };
				}, {});

				activeStep = state.steps.reduce((active, step) => {
					if (active) {
						return active;
					}

					if (!stepsCompletedMap[step.name]) {
						return step;
					}

					return active;
				}, null);

				// if they STILL don't have an active step, they dun
				if (!activeStep) {
					isFinished = true;
				}
			}

			return {
				...state,
				activeStep,
				isFinished,
				isViewingPreviousStep: false,
			};
		}

		case PREVIOUS_STEP: {
			let activeStep = state.activeStep;
			const { steps } = state;

			if (activeStep.index > 0) {
				activeStep = steps[activeStep.index - 1];
			}

			return {
				...state,
				activeStep,
				isViewingPreviousStep: true,
			};
		}

		case ADD_EDUCATION_OCCUPATION_STEP: {
			let steps = cloneDeep(state.steps);
			const isQuestionsStep = (step) => step.name === STEPS.QUESTIONS_CONVO;
			const indexOfQuestions = findIndex(steps, isQuestionsStep);

			// this is a hack to add profile details back into the steps before questions
			// to show education and occupation for users in the education occupation experiment
			const educationOccupationStep = {
				description: "About you",
				hasIntro: false,
				name: STEPS.EDUCATION_OCCUPATION,
				preview: "About you",
				statname: "profile_details",
			};

			if (indexOfQuestions > -1) {
				steps.splice(indexOfQuestions, 0, educationOccupationStep);
			} else {
				// this should never happen but just in case questions are removed in the future
				steps.push(educationOccupationStep);
			}


			// these things have a fucking separate index field
			// that we have to update now
			steps = steps.map((step, index) => ({ ...step, index }));

			return {
				...state,
				steps,
			};
		}

		case SUBMIT_QUESTION_RESPONSE: {
			return state;
		}

		case NEXT_ON_STEP_INTRO: {
			return state;
		}

		case NEXT_ON_QUESTION_INTRO: {
			return state;
		}

		case RECORD_QUESTION_RESPONSE: {
			return state;
		}

		case SUBMIT_QUESTION_CARD_RESPONSE: {
			return state;
		}

		case UPDATE_WIW_FIELD_DATA: {
			let wiwFieldData = state.wiwFieldData;

			wiwFieldData = {
				...wiwFieldData,
				...action.fieldData,
			};

			return {
				...state,
				wiwFieldData,
			};
		}

		case COMPLETE_ONBOARDING_SUCCESS: {
			return {
				...state,
				isCompleted: true,
				isCompletedError: "",
			};
		}

		case COMPLETE_ONBOARDING_FAILURE: {
			let errorMsg;

			if (action.requirements) {
				// parse requirements and add that into error
				errorMsg = getRequirementSentence(action.requirements);
			} else if (typeof action.error === "string") {
				errorMsg = action.error;
			}

			if (!errorMsg) {
				errorMsg = _str("Oops, something went wrong!");
			}

			return {
				...state,
				isCompletedError: errorMsg,
			};
		}

		case FINISH: {
			if (isApp()) {
				window.App.loadPage(action.url, {
					clear_history: 1,
					cf: "onboarding",
				});
			} else {
				window.location.href = action.url;
			}

			return state;
		}

		case SHOW_ERROR: {
			return {
				...state,
				error: action.message,
			};
		}

		case SAVE_CROP:
		case UPLOAD_FILE_SUCCESS: {
			let userThumb = state.userThumb;

			if (action.thumb) {
				userThumb = action.thumb;
			} else if (action.selectedPhoto && action.selectedPhoto.thumb) {
				userThumb = action.selectedPhoto.thumb;
			}

			return {
				...state,
				userThumb,
				hasSelectedPhoto: true,
			};
		}

		case UPLOAD_DETAILS_SUCCESS:
			if (action.thumb) {
				return {
					...state,
					userThumb: action.thumb,
				};
			}

			return state;

		case RESET_UPLOADER: {
			return {
				...state,
				hasSelectedPhoto: false,
			};
		}

		case UPDATE_REQUIREMENT_PROGRESS: {
			const numRequirementsLeft = action.requirementCount - action.activityCount;
			const connectorString = numRequirementsLeft === action.requirementCount ? " " : " more ";
			const requirement = REQUIREMENTS[state.activeStep.name];
			let requirementName;

			if (requirement && requirement.name) {
				requirementName = numRequirementsLeft === 1 ?
					requirement.name.singular : requirement.name.plural;
			}

			const progress = [
				numRequirementsLeft,
				connectorString,
				requirementName,
			].join(" ");

			return {
				...state,
				activityCount: {
					...state.activityCount,
					[state.activeStep.name]: action.activityCount,
				},
				requirementProgress: {
					...state.requirementProgress,
					[state.activeStep.name]: {
						requirement: progress,
					},
				},
			};
		}

		case REORDER_PHOTOS_SUCCESS: {
			return {
				...state,
				user: {
					...state.user,
					thumbs: action.thumbs,
				},
				userThumb: action.thumbs[0][USER_THUMB_SIZE],
			};
		}

		case COMPLETE_QUESTION_CARDS: {
			return state;
		}

		case SET_ACTIVE_STEP: {
			return {
				...state,
				activeStep: action.activeStep,
			};
		}

		default: {
			return state;
		}
	}
}


// ////////////////////////
// ACTION CREATORS ///////
// //////////////////////

export function initializeOnboarding(payload) {
	return {
		type: INITIALIZE_ONBOARDING,
		payload,
	};
}

export function nextStep(params) {
	return {
		type: NEXT_STEP,
		params,
	};
}

export function previousStep() {
	return {
		type: PREVIOUS_STEP,
	};
}

export function completeOnboarding(isReboarding) {
	return (dispatch, getStore) => {
		const store = getStore().onboarding;
		const { configName, steps } = store;

		// mark the userlog as finished
		if (steps.length) {
			OnboardingApi.finishUserlog(configName, steps)
				.then(() => {
					if (isApp()) {
						AppPhoneCommand("onboardingComplete");

						if (isReboarding) {
							AppPhoneCommand("enableInAppNotifications");
						}
					}
				});
		}

		OnboardingApi.turnOffProvisional()
			.then(() => {
				if (!isReboarding) {
					OnboardingApi.activateAccountAPI(); // fire and forget for now
				}

				dispatch({
					type: COMPLETE_ONBOARDING_SUCCESS,
				});
			})
			.catch((error) => {
				dispatch({
					type: COMPLETE_ONBOARDING_FAILURE,
					error,
				});
			});
	};
}

export function activateAccount() {
	return () => {
		// CURRENTLY UNUSED

		// const store = getStore().onboarding;
		// const { configName, steps } = store;

		// // mark the userlog as finished
		// if (steps.length) {
		// 	finishUserlog(configName, steps);
		// }

		// activateAccountAPI(); // fire and forget for now
		// .then((res) => {
		// 	if (res.status === 0 || res.status === "0") {
		// 		dispatch({
		// 			type: COMPLETE_ONBOARDING_SUCCESS,
		// 		});

		// 		dispatch({ type: NEXT_STEP });
		// 	} else {
		// 		dispatch({
		// 			type: COMPLETE_ONBOARDING_FAILURE,
		// 			requirements: res.requirements,
		// 		});
		// 	}
		// })
		// .catch((error) => {
		// 	dispatch({
		// 		type: COMPLETE_ONBOARDING_FAILURE,
		// 		error,
		// 	});
		// });
	};
}

export function finish() {
	return (dispatch, getStore) => {
		const store = getStore().onboarding;

		dispatch({
			type: FINISH,
			url: store.redirect.url,
		});
	};
}

export function updateProgress(requirementCount, activityCount) {
	return {
		type: UPDATE_REQUIREMENT_PROGRESS,
		requirementCount,
		activityCount,
	};
}

// Analytics: this records all the responses the user selects
export function recordQuestionResponse(response) {
	return {
		type: RECORD_QUESTION_RESPONSE,
		response,
	};
}

// Analytics: this records the actual responses the user submits
export function submitQuestionResponse(questionResponse) {
	return {
		type: SUBMIT_QUESTION_RESPONSE,
		questionResponse,
	};
}

export function completeQuestionCards() {
	return {
		type: COMPLETE_QUESTION_CARDS,
	};
}

export function nextOnQuestionIntro() {
	return {
		type: NEXT_ON_QUESTION_INTRO,
	};
}

export function nextOnStepIntro() {
	return {
		type: NEXT_ON_STEP_INTRO,
	};
}

export function nextOnWiwField(currentFieldData) {
	return {
		type: NEXT_ON_WIW_FIELD,
		currentFieldData,
	};
}

export function previousOnWiwField() {
	return {
		type: PREVIOUS_ON_WIW_FIELD,
	};
}

export function updateWiwFieldData(fieldData) {
	return {
		type: UPDATE_WIW_FIELD_DATA,
		fieldData,
	};
}

export function submitQuestionCardResponse(data) {
	return {
		type: SUBMIT_QUESTION_CARD_RESPONSE,
		data,
	};
}

export function addEducationOccupationSteps() {
	return {
		type: ADD_EDUCATION_OCCUPATION_STEP,
	};
}

export function reloadOnboardingPage() {
	const destination = "/onboarding";

	if (isApp()) {
		window.App.loadPage(destination, {
			post_login: 1,
			clear_history: 1,
		});
	} else {
		window.location.href = destination;
	}
}

export function setActiveStep() {
	return (dispatch, getStore) => {
		const store = getStore().onboarding;

		// No steps left, just take them to the finish line.
		if (store.steps.length === 0) {
			dispatch(nextStep());

			return;
		}

		if (store.configName === "MINI_ONBOARDING") {
			dispatch({
				type: SET_ACTIVE_STEP,
				activeStep: store.steps[0],
			});

			return;
		}

		// check their completion status from the backend
		OnboardingApi.getCompletionStatus()
			.then((res) => {
				const phoneStep = store.steps.find((field) => field.name === STEPS.PHONE);

				// if they have incompleted requirements, show that step
				if (res && res.requirements) {
					dispatch({
						type: SET_ACTIVE_STEP,
						activeStep: getFirstIncompletedStep(res.requirements, store.steps),
					});
				} else if (res.status === 0 && phoneStep) {
					// if they have completed onboarding and they are in the sms experiment take them to sms
					// The sms step is not part of requirements because it is an experiment
					dispatch({
						type: SET_ACTIVE_STEP,
						activeStep: phoneStep,
					});
				} else {
					dispatch({
						type: SET_ACTIVE_STEP,
						activeStep: store.steps[0],
					});
				}
			})
			.catch(() => {
				return dispatch({
					type: SET_ACTIVE_STEP,
					activeStep: store.steps[0],
				});
			});
	};
}

export function checkCompletionAndMaybeNextStep() {
	return (dispatch) => {
		OnboardingApi.getCompletionStatus()
			.then((res) => {
				if (res && res.requirements) {
					return dispatch({
						type: COMPLETE_ONBOARDING_FAILURE,
						requirements: res.requirements,
					});
				} else {
					return dispatch({ type: NEXT_STEP });
				}
			})
			.catch((error) => {
				return dispatch({
					type: COMPLETE_ONBOARDING_FAILURE,
					error,
				});
			});
	};
}



// WEBPACK FOOTER //
// ./src/ducks/onboarding.js